// DailyProductionTableModal.js
import React, { useState } from "react";
import ExportButtons from "./ExportButtons";

const DailyProductionTableModal = ({ data, onClose, orderTotals = {} }) => {
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split("T")[0]);

  const filtered = (data || []).filter(row => row.date === selectedDate);
  const grouped = {};
  const completedByOrder = {};

  filtered.forEach(entry => {
    const order = entry.cut_order_no || `Order-${entry.order_id}`;
    const color = entry.suborder_colour_name || `Color-${entry.sub_order_id}`;
    const line = entry.line?.trim() || "No Line Info";

    if (!grouped[order]) grouped[order] = {};
    if (!grouped[order][line]) grouped[order][line] = {};
    if (!grouped[order][line][color]) grouped[order][line][color] = [];

    grouped[order][line][color].push(entry);

    if (!completedByOrder[order]) completedByOrder[order] = {};
    if (!completedByOrder[order][color]) completedByOrder[order][color] = 0;
    completedByOrder[order][color] += entry.total_piece;
  });

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
      <div className="bg-white w-full h-full p-6 overflow-y-auto relative rounded-none shadow-none">
        <button
          className="absolute top-4 right-6 text-2xl text-gray-500 hover:text-red-500 z-50"
          onClick={onClose}
        >
          ✖
        </button>

        <div className="flex items-center justify-between mb-6 pr-10">
          <div>
            <h2 className="text-3xl font-bold text-indigo-800">🏭 Production - Daily Report</h2>
            <p className="text-sm text-gray-700 mt-1">
              📅 Date: <span className="font-semibold">{selectedDate}</span>
            </p>
            <div className="mt-2">
              <label className="text-sm font-medium text-gray-700 mr-2">Select Date:</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border px-3 py-1 rounded-md shadow-sm"
              />
            </div>
          </div>
          <ExportButtons data={filtered} filename="Production_Report" elementId="printable-production-table" />
        </div>

        <div id="printable-production-table" className="px-4">
          {Object.entries(orderTotals).map(([order, colorsPlanned]) => {
            const orderTotalPlanned = Object.values(colorsPlanned).reduce((a, b) => a + b, 0);
            const orderTotalCompleted = Object.entries(colorsPlanned).reduce((sum, [color, planned]) => {
              const lines = grouped[order] || {};
              const entriesByLine = Object.entries(lines).reduce((acc, [line, colors]) => {
                if (colors[color]) acc.push(...colors[color]);
                return acc;
              }, []);
              return sum + entriesByLine.reduce((s, row) => s + row.total_piece, 0);
            }, 0);
            const orderTotalPending = Math.max(orderTotalPlanned - orderTotalCompleted, 0);

            return (
              <div key={order} className="mb-10 border-t border-gray-300 pt-4">
                <h3 className="text-lg font-bold text-white bg-indigo-600 px-4 py-2 rounded-md shadow-md">
                  Order No: {order}
                </h3>
                <div className="text-sm text-gray-800 px-4 py-2 bg-gray-50 border rounded-b mb-4">
                  📦 <strong>Order Quantity:</strong> {orderTotalPlanned} &nbsp; | &nbsp;
                  ✅ <strong>Completed:</strong> {orderTotalCompleted} &nbsp; | &nbsp;
                  ⏳ <strong>Pending:</strong> {orderTotalPending}
                </div>

                {Object.entries(colorsPlanned).map(([color, planned]) => {
                  const lines = grouped[order] || {};
                  const entriesByLine = Object.entries(lines).reduce((acc, [line, colors]) => {
                    if (colors[color]) acc.push({ line, entries: colors[color] });
                    return acc;
                  }, []);

                  const totalCompleted = entriesByLine.reduce((sum, line) => {
                    return sum + line.entries.reduce((s, row) => s + row.total_piece, 0);
                  }, 0);

                  const pending = Math.max(planned - totalCompleted, 0);

                  return (
                    <div key={color} className="mb-6 border border-gray-200 rounded-lg shadow bg-white">
                      <div className="bg-indigo-100 px-4 py-2 rounded-t-md flex flex-wrap justify-between">
                        <div className="text-sm font-semibold text-indigo-900">
                          🎨 Color: <span className="bg-white px-2 py-1 rounded text-indigo-700 font-medium">{color}</span>
                        </div>
                        <div className="text-sm text-gray-700 flex gap-4">
                          <span>✅ Completed: <strong>{totalCompleted}</strong></span>
                          <span>📦 Order Quantity: <strong>{planned}</strong></span>
                          <span>⏳ Pending: <strong>{pending}</strong></span>
                        </div>
                      </div>

                      <table className="w-full text-sm">
                        <thead className="bg-indigo-200 text-indigo-900 font-semibold">
                          <tr>
                            <th className="px-4 py-2 border">Job No</th>
                            <th className="px-4 py-2 border">Line</th>
                            <th className="px-4 py-2 border">Total Pieces</th>
                            <th className="px-4 py-2 border">Sizes</th>
                            <th className="px-4 py-2 border">Remark</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entriesByLine.length > 0 ? (
                            entriesByLine.flatMap(({ line, entries }, idx) => (
                              entries.map((row, i) => (
                                <tr key={`${idx}-${i}`} className="hover:bg-indigo-50 transition-all">
                                  <td className="px-4 py-2 border text-gray-800">{row.job_no}</td>
                                  <td className="px-4 py-2 border text-gray-800">{line}</td>
                                  <td className="px-4 py-2 border text-center font-bold text-green-700">{row.total_piece}</td>
                                  <td className="px-4 py-2 border text-gray-700">
                                    <div className="flex flex-wrap gap-2">
                                      {Object.entries(row.size_variant_quantity || {}).map(([size, qty]) => (
                                        <span
                                          key={size}
                                          className="bg-yellow-100 text-gray-800 text-xs px-2 py-1 rounded shadow-sm"
                                        >
                                          {size}: {qty}
                                        </span>
                                      ))}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 border text-gray-600 italic">{row.remark || "-"}</td>
                                </tr>
                              ))
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="text-center py-3 italic text-gray-500">
                                No production entries. Status: Pending
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <p className="text-center text-gray-500 mt-6 italic">
              No Production data available for selected date.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyProductionTableModal;